import { FormControl, FormHelperText, Input, InputAdornment, InputLabel, MenuItem, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import Downshift from "downshift";
import PropTypes from "prop-types";
import { useState } from "react";

const StyledSuggestionsContainer = styled("div")(() => ({
    flexGrow: 1,
    position: "relative",
}));

const StyledInput = styled(Input)(({ theme }) => ({
    borderRadius: 4,
    border: "1px solid #E0E0E0",
    fontSize: 14,
    padding: "4px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:focus": {
        borderColor: "#80bdff",
        boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
    },
}));

const StyledFormControl = styled(FormControl)(() => ({
    marginBottom: "20px",
    width: "100%",
}));

const StyledPaper = styled(Paper)(() => ({
    position: "absolute",
    zIndex: 3,
    left: 0,
    right: 0,
    top: "61px",
    height: "210px",
    overflowY: "auto",
}));

const StyledMenuItemTitle = styled("span")({
    fontSize: "14px",
    margin: 0,
    padding: 0,
});

const StyledMenuItemSubtext = styled("span")({
    fontSize: "12px",
    margin: 0,
    padding: 0,
});

const StyledMenuItem = styled("div")({
    width: "100%",
    lineHeight: "1em",
});

const StyledResultType = styled("div")({
    float: "right",
    textTransform: "uppercase",
    fontSize: "13px",
    fontWeight: 600,
    color: "#9E9E9E",
});

const StyledInputLabel = styled(InputLabel)({
    fontSize: "13px",
    textTransform: "uppercase",
});

const StyledFormHelperText = styled(FormHelperText)(({ required }) => ({
    fontStyle: "italic",
    margin: "0px",

    ...(required && {
        color: "dimgrey",
    })
}));

function renderInput(inputProps) {
    const { endAdornment, error, InputProps, label, ref, startAdornment, errorText, helperText, helperTextRequired } = inputProps;

    return (
        <StyledFormControl>
            {label && <StyledInputLabel shrink>{label}</StyledInputLabel>}
            <StyledInput
                inputRef={ref}
                disableUnderline={true}
                autoComplete={"no"}
                style={error ? { borderColor: "red" } : {}}
                endAdornment={endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                startAdornment={startAdornment && <InputAdornment position="start">{startAdornment}</InputAdornment>}
                {...InputProps}
            />
            {error ? (
                <StyledFormHelperText error={true}>{errorText}</StyledFormHelperText>
            ) : (
                <StyledFormHelperText required={helperTextRequired}>
                        {helperText}
                </StyledFormHelperText>
            )}
        </StyledFormControl>
    );
}

function renderSuggestion({
    suggestion,
    index,
    itemProps,
    highlightedIndex,
    selectedItem,
    key,
    display,
    displaySub,
    displayType,
}) {
    const isHighlighted = highlightedIndex === index;
    const isSelected = selectedItem ? (String(selectedItem[key]) || "").indexOf(suggestion[key]) > -1 : false;

    return (
        <MenuItem
            {...itemProps}
            key={key}
            selected={isHighlighted}
            component="div"
            className="waterloo"
            style={{
                fontWeight: isSelected ? 500 : 400,
            }}
        >
            <StyledMenuItem>
                <StyledMenuItemTitle>{display(suggestion)}</StyledMenuItemTitle>
                <br />
                <StyledMenuItemSubtext>{displaySub && displaySub(suggestion)}</StyledMenuItemSubtext>
            </StyledMenuItem>
            <StyledResultType>{displayType && <div>{displayType(suggestion)} Display Type</div>}</StyledResultType>
        </MenuItem>
    );
}

renderSuggestion.propTypes = {
    highlightedIndex: PropTypes.number,
    index: PropTypes.number,
    itemProps: PropTypes.object,
    selectedItem: PropTypes.string,
    suggestion: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

const Autocomplete = (props) => {
    const {
        id,
        error,
        label,
        options,
        disabled,
        placeholder,
        suggestions,
        itemToString,
        endAdornment,
        freeFormValue,
        startAdornment,
        initialInputValue,
        errorText,
        helperText,
        searchFunction,
        freeformUpdate,
        onSelect,
        helperTextRequired
    } = props;
    const [itemSelected, setItemSelected] = useState(true);
    const [inputValFromState, setInputValFromState] = useState(null);
    const [focused, setFocused] = useState(false);

    const handleInputValueChange = (value) => {
        if (focused) {
            setItemSelected(false);
            setInputValFromState(value);
            searchFunction(value);
            freeformUpdate(value);
        }
    };

    const handleSelect = (item) => {
        setItemSelected(true);
        setInputValFromState(null);
        onSelect(item);
    };

    const handleBlur = (prevVal) => {
        setItemSelected(false);
        setInputValFromState(prevVal);
        setFocused(false);
    };

    const handleFocus = () => {
        setFocused(true);
    };

    return (
        <div>
            <Downshift
                id={id}
                onSelect={handleSelect}
                itemToString={itemToString}
                onInputValueChange={handleInputValueChange}
                initialInputValue={initialInputValue}
            >
                {({
                    isOpen,
                    inputValue,
                    getItemProps,
                    getMenuProps,
                    selectedItem,
                    getInputProps,
                    getRootProps,
                    highlightedIndex,
                }) => {
                    return (
                        <StyledSuggestionsContainer {...getRootProps()}>
                            {renderInput({
                                fullWidth: true,
                                endAdornment: endAdornment,
                                startAdornment: startAdornment,
                                errorText: errorText,
                                helperText: helperText,
                                helperTextRequired: helperTextRequired,
                                id: id,
                                label: label,
                                error: error,
                                InputProps: getInputProps({
                                    placeholder: placeholder,
                                    value: itemSelected ? initialInputValue : inputValFromState,
                                    disabled: disabled,
                                    onBlur: () => handleBlur(inputValFromState),
                                    onFocus: () => handleFocus(),
                                }),
                            })}
                            <div {...getMenuProps()}>
                                {isOpen && suggestions.length > 0 ? (
                                    <StyledPaper square>
                                        {suggestions &&
                                            suggestions.map((suggestion, index) =>
                                                renderSuggestion({
                                                    suggestion,
                                                    index,
                                                    itemProps: getItemProps({ item: suggestion }),
                                                    highlightedIndex,
                                                    selectedItem: !freeFormValue ? null : selectedItem,
                                                    key: suggestion[options.key],
                                                    display: options.display,
                                                    displaySub: options.displaySub,
                                                    displayType: options.displayType,
                                                })
                                            )}
                                    </StyledPaper>
                                ) : null}
                            </div>
                        </StyledSuggestionsContainer>
                    );
                }}
            </Downshift>
        </div>
    );
};

export default Autocomplete;
